import React, { useCallback, useEffect, useRef, useState } from "react";
import { getQuestionsForMe } from "../../api/agent";

const RELOAD_INTERVAL_MS = 60000;

const PERSONA_LABELS = {
  vincent: "Vincent",
  virginie: "Virginie",
  bruno: "Bruno",
  quentin: "Quentin",
  unknown: "Inconnu",
};

function personaLabel(persona) {
  return PERSONA_LABELS[persona] || persona;
}

function formatDate(valeur) {
  if (!valeur) return null;
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Depuis quand on attend — en disant la vérité quand la question a été relancée.
 *
 * `asked_at` est remis à maintenant chaque fois qu'on repose la question, sans
 * quoi elle ne réapparaîtrait pas dans le bandeau. Mais l'afficher seul faisait
 * lire « depuis le 24/08 » sur une demande du 1er août : celui qui ouvre son
 * bandeau ne pouvait pas voir qu'une question dormait depuis trois semaines,
 * alors que c'est exactement ce qui devrait le faire agir.
 *
 * Les deux dates ne sont distinguées qu'au jour près : une question posée et
 * adressée le même jour — le cas courant — garde le libellé simple.
 */
function askedAtLabel(askedAt, createdAt) {
  const relance = formatDate(askedAt);
  const creation = formatDate(createdAt);
  if (!relance) return creation ? `depuis le ${creation}` : "";
  if (!creation || creation === relance) return `depuis le ${relance}`;
  return `demande du ${creation}, relancée le ${relance}`;
}

/**
 * Charge les questions qui attendent la réponse de la personne connectée.
 *
 * Renvoie `[questions, reload]`. `reload` doit être appelé après un
 * commentaire : sans lui, la question resterait affichée jusqu'au prochain
 * rechargement de page alors qu'on vient d'y répondre.
 *
 * Volontairement distinct du compteur de messages non lus : celui-là s'éteint
 * dès qu'on ouvre le chat, répondu ou pas.
 */
export function useQuestionsForMe(token) {
  const [questions, setQuestions] = useState([]);
  const mounted = useRef(true);
  // Numéro de la dernière demande partie : rien n'ordonne les réponses, et
  // `setQuestions` appliquerait sinon la dernière ARRIVÉE. Un tic périodique
  // parti avant l'envoi d'un commentaire pourrait donc réafficher la question
  // à laquelle on vient de répondre.
  const derniere = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(() => {
    derniere.current += 1;
    const moi = derniere.current;
    // `token` est optionnel : l'appel retombe sur celui du navigateur, comme
    // le compteur de messages non lus du tableau de bord.
    // `Promise.resolve` enveloppe un retour inattendu (undefined) : un
    // bandeau d'information ne doit jamais faire tomber l'écran qui le porte.
    return Promise.resolve(getQuestionsForMe(token))
      .then((d) => {
        if (!d) return;
        if (moi !== derniere.current) return;  // une demande plus récente a pris la main
        if (mounted.current) setQuestions(d.questions || []);
      })
      .catch(() => {
        /* silencieux : une liste vide vaut mieux qu'une erreur bloquante */
      });
  }, [token]);

  // Rafraîchissement périodique, comme le compteur de messages non lus. Sans
  // lui, une panne réseau passagère laisse le bandeau vide jusqu'à une
  // navigation complète — sur une fonctionnalité dont le but est justement
  // qu'une question ne reste pas invisible.
  useEffect(() => {
    reload();
    const t = setInterval(reload, RELOAD_INTERVAL_MS);
    return () => clearInterval(t);
  }, [reload]);

  return [questions, reload];
}

export default function PendingQuestions({ questions, onOpen }) {
  if (!questions || !questions.length) return null;

  return (
    <section className="pending-questions mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <h2 className="text-sm font-semibold text-amber-900">
        ❓ On attend ta réponse ({questions.length})
      </h2>
      <p className="mt-1 text-xs text-amber-800">
        Réponds en commentaire sur la demande — ça suffit à la faire disparaître d'ici.
      </p>
      <ul className="mt-3 flex flex-col gap-1">
        {questions.map((q) => (
          <li key={q.id}>
            <button
              type="button"
              onClick={() => onOpen && onOpen(q.id)}
              className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-left text-sm text-gray-900 hover:border-amber-400"
            >
              <span className="block font-medium">{q.title}</span>
              <span className="mt-1 block text-xs text-gray-500">
                {(q.requesters || []).length > 0 && (
                  <>Demandé par {q.requesters.map(personaLabel).join(" et ")}</>
                )}
                {(q.requesters || []).length > 0 &&
                  Boolean(askedAtLabel(q.asked_at, q.created_at)) &&
                  " · "}
                {askedAtLabel(q.asked_at, q.created_at)}
                {(q.also_awaiting || []).length > 0 && (
                  <> · aussi demandé à {q.also_awaiting.map(personaLabel).join(" et ")}</>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
